import { useAuth } from '@/context/AuthContext'

/**
 * Role-based UI guards. Mirrors the backend `require_role` decorators
 * in `server/api/dependencies/auth.py` so the frontend hides actions
 * the API would otherwise reject. The backend remains authoritative —
 * these flags only stop the user from seeing buttons that wouldn't work.
 *
 * Levels (matches CLAUDE.md "API Endpoint Auth Levels"):
 *   - viewer    : read-only (no write actions)
 *   - manager   : create/update operators, execute commands, install tokens
 *   - admin     : everything above + delete operators, user management
 */
export function usePermissions() {
  const { user } = useAuth()
  const role = user?.role

  const isAdmin = role === 'admin'
  const isManager = role === 'manager'
  const isViewer = role === 'viewer'

  return {
    role,
    isAdmin,
    isManager,
    isViewer,
    /** Can run remote commands and edit operator config */
    canWrite: isAdmin || isManager,
    /** Can delete operators / users */
    canDelete: isAdmin,
    /** Can manage dashboard users */
    canManageUsers: isAdmin,
    /** Can onboard new operators (create + install token) */
    canOnboard: isAdmin || isManager,
  }
}
